import {
  MANUAL_RETURN_REASON_MAX,
  SHIPMENT_STAGE_LABELS,
  type Id,
  type ManualReturnCandidate,
  type WriteRejection,
} from "@estelle/domain";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useEffect, useId, useState } from "react";

import { useDataSource } from "@/adapters/data-source";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

/**
 * Add a return by hand (#594): for a parcel that came back without the courier
 * marking it RTO. Find the order by its number, pick the shipment, say why.
 *
 * Adding only puts the shipment under Returns to receive, tagged as added
 * manually; the ordinary receive confirm does the rest, and nothing goes to
 * Shopify until it runs. A shipment that cannot be added is shown with the
 * reason, not hidden.
 */
export function AddReturnDialog({
  open,
  onOpenChange,
  initialOrderNumber = "",
  onAdded,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Starts the search for this order, as from the order page. */
  initialOrderNumber?: string;
  onAdded?: (shipmentId: Id) => void;
}) {
  const data = useDataSource();
  const queryClient = useQueryClient();
  const searchId = useId();
  const reasonId = useId();
  const [term, setTerm] = useState(initialOrderNumber);
  const [debounced, setDebounced] = useState(initialOrderNumber);
  const [orderId, setOrderId] = useState<Id | null>(null);
  const [shipmentId, setShipmentId] = useState<Id | null>(null);
  const [reason, setReason] = useState("");
  const [rejection, setRejection] = useState<WriteRejection | null>(null);

  useEffect(() => {
    const handle = setTimeout(() => {
      setDebounced(term.trim());
    }, 250);
    return () => {
      clearTimeout(handle);
    };
  }, [term]);

  const search = useQuery({
    queryKey: ["returns", "manual", "candidates", debounced],
    queryFn: () => data.returns.findManualCandidates(debounced),
    enabled: open && debounced.length >= 2,
  });

  const candidates: ManualReturnCandidate[] = search.data ?? [];
  // From the order page the order is known: choose it as soon as it is found.
  const exact =
    initialOrderNumber !== ""
      ? candidates.find(
          (candidate) => candidate.orderNumber === initialOrderNumber,
        )
      : undefined;
  const chosen =
    candidates.find((candidate) => candidate.orderId === orderId) ?? exact;
  const chosenShipment = chosen?.shipments.find(
    (shipment) => shipment.id === shipmentId,
  );

  const mutation = useMutation({
    mutationFn: () => {
      if (shipmentId === null) throw new Error("no shipment chosen");
      return data.returns.addManual(shipmentId, reason);
    },
    onSuccess: (result) => {
      if (result.ok) {
        setRejection(null);
        void queryClient.invalidateQueries({ queryKey: ["returns"] });
        void queryClient.invalidateQueries({ queryKey: ["orders"] });
        onAdded?.(result.data.shipmentId);
        onOpenChange(false);
      } else {
        setRejection(result.rejection);
        void queryClient.invalidateQueries({
          queryKey: ["returns", "manual"],
        });
      }
    },
  });

  const reasonError =
    rejection?.kind === "invalid" ? rejection.fields?.reason : undefined;
  const busy = mutation.isPending;
  const ready =
    chosenShipment !== undefined &&
    chosenShipment.refusal === null &&
    reason.trim() !== "";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        title="Add a return"
        description="For a parcel that came back without the courier marking it RTO."
      >
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor={searchId} className="text-sm font-medium">
              Order number
            </Label>
            <Input
              id={searchId}
              autoFocus={initialOrderNumber === ""}
              value={term}
              placeholder="For example EL1123"
              disabled={busy}
              onChange={(event) => {
                setTerm(event.target.value);
                setOrderId(null);
                setShipmentId(null);
              }}
            />
          </div>

          {debounced.length >= 2 && search.isPending ? (
            <p className="text-muted-foreground text-sm">Looking…</p>
          ) : null}
          {search.isError ? (
            <p role="alert" className="text-destructive text-sm">
              {search.error.message}
            </p>
          ) : null}
          {search.isSuccess && candidates.length === 0 ? (
            <p className="text-muted-foreground text-sm">
              No order has that number.
            </p>
          ) : null}

          {candidates.length > 0 && !chosen ? (
            <ul
              className="divide-y rounded-md border"
              aria-label="Orders found"
            >
              {candidates.map((candidate) => (
                <li key={candidate.orderId}>
                  <button
                    type="button"
                    className="hover:bg-accent flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm"
                    onClick={() => {
                      setOrderId(candidate.orderId);
                      setShipmentId(null);
                    }}
                  >
                    <span className="font-medium">{candidate.orderNumber}</span>
                    <span className="text-muted-foreground text-xs">
                      {candidate.cancelled ? "Cancelled · " : ""}
                      {candidate.shipments.length}{" "}
                      {candidate.shipments.length === 1
                        ? "shipment"
                        : "shipments"}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          ) : null}

          {chosen ? (
            <fieldset className="space-y-2">
              <legend className="text-sm font-medium">
                Which shipment of {chosen.orderNumber} came back?
              </legend>
              {chosen.shipments.length === 0 ? (
                <p className="text-muted-foreground text-sm">
                  This order has no shipment.
                </p>
              ) : null}
              {chosen.shipments.map((shipment) => (
                <label
                  key={shipment.id}
                  className={cn(
                    "flex items-start gap-2 rounded-md border p-2.5 text-sm",
                    shipment.refusal
                      ? "bg-muted/40 text-muted-foreground"
                      : "hover:bg-accent cursor-pointer",
                  )}
                >
                  <input
                    type="radio"
                    name="manual-return-shipment"
                    className="mt-1"
                    disabled={shipment.refusal !== null || busy}
                    checked={shipmentId === shipment.id}
                    onChange={() => {
                      setShipmentId(shipment.id);
                    }}
                  />
                  <span className="min-w-0">
                    <span className="block font-medium">
                      {shipment.name ?? "Shipment"}
                      {shipment.courier ? ` · ${shipment.courier}` : ""}
                    </span>
                    <span className="text-muted-foreground block text-xs wrap-anywhere">
                      {shipment.trackingNumber ?? "No tracking number"} ·{" "}
                      {shipment.stage
                        ? SHIPMENT_STAGE_LABELS[shipment.stage]
                        : "No tracking stage"}
                    </span>
                    {shipment.refusal ? (
                      <span className="block text-xs">{shipment.refusal}</span>
                    ) : null}
                  </span>
                </label>
              ))}
              {exact === undefined ? (
                <Button
                  type="button"
                  variant="link"
                  size="sm"
                  className="px-0"
                  onClick={() => {
                    setOrderId(null);
                    setShipmentId(null);
                  }}
                >
                  Choose a different order
                </Button>
              ) : null}
            </fieldset>
          ) : null}

          {chosenShipment ? (
            <div className="space-y-1.5">
              <Label htmlFor={reasonId} className="text-sm font-medium">
                Why is it being added?
              </Label>
              <Input
                id={reasonId}
                value={reason}
                maxLength={MANUAL_RETURN_REASON_MAX}
                placeholder="One line, kept with the return"
                aria-invalid={reasonError ? true : undefined}
                disabled={busy}
                onChange={(event) => {
                  setReason(event.target.value);
                }}
              />
              {reasonError ? (
                <p role="alert" className="text-destructive text-xs">
                  {reasonError}
                </p>
              ) : null}
            </div>
          ) : null}

          {rejection && !reasonError ? (
            <p role="alert" className="text-destructive text-sm">
              {rejection.message}
            </p>
          ) : null}

          <div className="flex flex-col-reverse gap-2 border-t pt-3 sm:flex-row sm:justify-end">
            <Button
              type="button"
              variant="ghost"
              disabled={busy}
              onClick={() => {
                onOpenChange(false);
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              disabled={!ready || busy}
              onClick={() => {
                setRejection(null);
                mutation.mutate();
              }}
            >
              {busy ? <Loader2 className="animate-spin" /> : null}
              Add return
            </Button>
          </div>
          <p className="text-muted-foreground text-xs">
            Nothing is sent to Shopify until the return is received.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
